import type { LastRentalCart } from '@kgc/api-contract';
import { useEffect, useState } from 'react';
import { getLastRentalCart } from '../../../api/rental-clone';
import { getBergepById } from '../../../api/rental-equipment';
import { EquipmentCategory, EquipmentCondition, EquipmentStatus, type Equipment } from '../types';
import { useWizardStore } from '../wizard-store';

/**
 * `useLastRentalClone` — a partner legutóbbi bérlésének („Mint múltkor") klón-
 * logikája EGY helyen (FE-A4 / Wave-2 E-105 Phase 3). Single source of truth:
 * az equipment-step `RentalLastRentalBanner` ezt használja — a fetch + gép-ID-
 * feloldás (`getBergepById`) + `hydrateFromClone` itt él (a korábbi PartnerStep
 * `CloneRentalButton` superseded 2026-06-07: a banner az egyetlen belépési pont).
 *
 * - `partnerId` változásra fetcheli a `getLastRentalCart`-ot.
 * - `cart === null` → a partnernek nincs korábbi bérlése (a CTA-t rejtsd el).
 * - `applyClone()` → a cart gép-ID-it Equipment-té oldja és hydrate-eli a store-t.
 */
export interface UseLastRentalClone {
  cart: LastRentalCart | null;
  busy: boolean;
  applyClone: () => Promise<void>;
}

export function useLastRentalClone(partnerId: string | null): UseLastRentalClone {
  const hydrateFromClone = useWizardStore(s => s.hydrateFromClone);
  const [cart, setCart] = useState<LastRentalCart | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Partner-váltáskor azonnal töröljük az előző partner kosarát — különben az
    // új partner fetch-ének megérkezéséig a régi adat (dátum/darabszám) felvillan.
    setCart(null);
    if (!partnerId) return;
    let active = true;
    void getLastRentalCart(partnerId).then(result => {
      if (active) setCart(result);
    });
    return () => {
      active = false;
    };
  }, [partnerId]);

  const applyClone = async (): Promise<void> => {
    if (!cart) return;
    setBusy(true);
    try {
      // a gép-ID-k feloldása Equipment-té (a cart teljes Equipment-objektumot vár)
      const resolved = await Promise.all(
        cart.items.map(async item => {
          const { data } = await getBergepById(item.equipmentId);
          const equipment: Equipment = {
            id: data.id,
            name: data.name,
            serialNumber: data.serialNumber,
            category: (data.category as EquipmentCategory) ?? EquipmentCategory.POWER_TOOL,
            brand: data.brand,
            model: data.model,
            status: (data.status as EquipmentStatus) ?? EquipmentStatus.AVAILABLE,
            condition: (data.condition as EquipmentCondition) ?? EquipmentCondition.GOOD,
            dailyRate: data.dailyRate,
            weeklyRate: data.weeklyRate,
            monthlyRate: data.monthlyRate,
            depositAmount: data.depositAmount,
          };
          return { equipment, quantity: item.quantity };
        })
      );

      hydrateFromClone({
        equipments: resolved,
        packages: cart.packages.map(pkg => ({
          equipmentId: pkg.equipmentId,
          packageId: pkg.packageId,
          // A BE-3 LastRentalPackage csak packageId-t ad (nem nevet/kódot) — a
          // hydrate a packageId-re re-szelektál; a display-név a step-2 render.
          packageName: '',
          packageCode: '',
          ...(pkg.qtyOverrides ? { qtyOverrides: pkg.qtyOverrides } : {}),
        })),
        period: {
          startDate: new Date(cart.period.startDate),
          endDate: new Date(cart.period.expectedEnd),
        },
      });
      // A hydrate után a kosár „elfogyott" → null, hogy a RentalLastRentalBanner
      // gate-je (`if (!cart) return null`) elrejtse a bannert. Enélkül „Ismétlés"
      // után bennmaradt + újra-kattintható volt → ismételt getBergepById+hydrate.
      setCart(null);
    } finally {
      setBusy(false);
    }
  };

  return { cart, busy, applyClone };
}
